export type NotificationSoundId = "default" | "assigned" | "chime" | "pop" | "pulse" | "glass";

export type NotificationSoundCategory = "external" | "internal";

export type NotificationSoundSettings = {
  enabled: boolean;
  defaultSound: NotificationSoundId;
  internalSound: NotificationSoundId;
  volume: number;
  integrationSounds: Record<string, NotificationSoundId | "inherit">;
  mutedCategories: NotificationSoundCategory[];
};

export const notificationSoundCategories: NotificationSoundCategory[] = ["external", "internal"];

export const notificationSoundIds: NotificationSoundId[] = [
  "default",
  "assigned",
  "chime",
  "pop",
  "pulse",
  "glass",
];

const defaultSettings: NotificationSoundSettings = {
  enabled: true,
  defaultSound: "default",
  internalSound: "chime",
  volume: 70,
  integrationSounds: {},
  mutedCategories: [],
};

type RawSettings = Partial<NotificationSoundSettings> | null | undefined;

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

let audioContext: AudioContext | null = null;

function hasTransientUserActivation(): boolean {
  return navigator.userActivation?.isActive ?? true;
}

function getAudioContext(): AudioContext | null {
  if (!audioContext && !hasTransientUserActivation()) {
    return null;
  }

  if (!audioContext) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    audioContext = new AudioContextClass();
  }

  return audioContext;
}

export async function prepareNotificationAudio(): Promise<void> {
  const ctx = getAudioContext();
  if (!ctx) {
    return;
  }

  if (ctx.state === "suspended") {
    if (!hasTransientUserActivation()) {
      return;
    }
    await ctx.resume();
  }

  const ringtone = getCallRingtoneAudio();
  ringtone.load();
}

function isSoundId(value: string | null | undefined): value is NotificationSoundId {
  return notificationSoundIds.includes(value as NotificationSoundId);
}

function clampVolume(value: number): number {
  return Math.max(0, Math.min(100, Number.isFinite(value) ? value : defaultSettings.volume));
}

export function normalizeNotificationSoundSettings(
  settings: RawSettings,
): NotificationSoundSettings {
  const integrationSounds =
    settings?.integrationSounds && typeof settings.integrationSounds === "object"
      ? Object.fromEntries(
          Object.entries(settings.integrationSounds)
            .map(([integrationId, sound]) => [
              integrationId,
              sound === "inherit" || sound === "default"
                ? "inherit"
                : isSoundId(String(sound))
                  ? sound
                  : "inherit",
            ])
            .filter(([integrationId]) => integrationId !== ""),
        )
      : {};

  const mutedCategories = Array.isArray(settings?.mutedCategories)
    ? settings.mutedCategories.filter((category): category is NotificationSoundCategory =>
        notificationSoundCategories.includes(category as NotificationSoundCategory),
      )
    : [];

  return {
    enabled: settings?.enabled !== false,
    defaultSound: isSoundId(settings?.defaultSound)
      ? settings.defaultSound
      : defaultSettings.defaultSound,
    internalSound: isSoundId(settings?.internalSound)
      ? settings.internalSound
      : defaultSettings.internalSound,
    volume: clampVolume(Number(settings?.volume ?? defaultSettings.volume)),
    integrationSounds,
    mutedCategories,
  };
}

export function isNotificationCategoryMuted(
  settings: RawSettings,
  category: NotificationSoundCategory,
): boolean {
  const normalized = normalizeNotificationSoundSettings(settings);
  return !normalized.enabled || normalized.mutedCategories.includes(category);
}

export function resolveNotificationSound(
  settings: RawSettings,
  companyIntegrationId: number | string | null | undefined,
): NotificationSoundId {
  const normalized = normalizeNotificationSoundSettings(settings);
  const integrationSound = companyIntegrationId
    ? normalized.integrationSounds[String(companyIntegrationId)]
    : null;

  return integrationSound && integrationSound !== "inherit"
    ? integrationSound
    : normalized.defaultSound;
}

type Tone = {
  frequency: number;
  start: number;
  duration: number;
  gain?: number;
  type?: OscillatorType;
};

const patterns: Record<NotificationSoundId, Tone[]> = {
  default: [{ frequency: 440, start: 0, duration: 0.38, gain: 0.2, type: "sine" }],
  assigned: [
    { frequency: 523.25, start: 0, duration: 0.16, gain: 0.18, type: "triangle" },
    { frequency: 659.25, start: 0.14, duration: 0.24, gain: 0.16, type: "triangle" },
  ],
  chime: [
    { frequency: 659.25, start: 0, duration: 0.16, gain: 0.16, type: "sine" },
    { frequency: 987.77, start: 0.12, duration: 0.26, gain: 0.12, type: "sine" },
  ],
  pop: [
    { frequency: 740, start: 0, duration: 0.08, gain: 0.18, type: "square" },
    { frequency: 520, start: 0.08, duration: 0.12, gain: 0.12, type: "sine" },
  ],
  pulse: [
    { frequency: 330, start: 0, duration: 0.12, gain: 0.15, type: "sine" },
    { frequency: 330, start: 0.18, duration: 0.16, gain: 0.14, type: "sine" },
  ],
  glass: [
    { frequency: 1046.5, start: 0, duration: 0.12, gain: 0.12, type: "sine" },
    { frequency: 1567.98, start: 0.04, duration: 0.18, gain: 0.09, type: "sine" },
  ],
};

function playTone(ctx: AudioContext, tone: Tone, volume: number): void {
  const now = ctx.currentTime + tone.start;
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.type = tone.type ?? "sine";
  oscillator.frequency.value = tone.frequency;
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime((tone.gain ?? 0.16) * (volume / 100), now + 0.02);
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + tone.duration);
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  oscillator.start(now);
  oscillator.stop(now + tone.duration + 0.03);
}

export async function playNotificationSound(
  sound: NotificationSoundId,
  volume: number,
): Promise<void> {
  const ctx = getAudioContext();
  if (!ctx) {
    return;
  }

  if (ctx.state === "suspended") {
    if (!hasTransientUserActivation()) {
      return;
    }
    await ctx.resume();
  }

  for (const tone of patterns[sound] ?? patterns.default) {
    playTone(ctx, tone, clampVolume(volume));
  }

  const maxDuration = Math.max(
    ...(patterns[sound] ?? patterns.default).map((tone) => tone.start + tone.duration),
  );
  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, Math.ceil(maxDuration * 1000) + 80);
  });
}

const incomingCallRingtoneUrl = "/incoming-call-ringtone.wav";
let callRingtoneAudio: HTMLAudioElement | null = null;
let activeCallRingtoneId: string | null = null;

function getCallRingtoneAudio(): HTMLAudioElement {
  if (!callRingtoneAudio) {
    callRingtoneAudio = new Audio(incomingCallRingtoneUrl);
    callRingtoneAudio.preload = "auto";
  }

  return callRingtoneAudio;
}

export async function playCallRingSound(volume: number): Promise<void> {
  const audio = new Audio(incomingCallRingtoneUrl);
  audio.volume = clampVolume(volume) / 100;
  await audio.play();
}

export function stopCallRingtone(id?: string | number): void {
  if (id !== undefined && activeCallRingtoneId !== String(id)) {
    return;
  }

  const audio = getCallRingtoneAudio();
  audio.pause();
  audio.currentTime = 0;
  activeCallRingtoneId = null;
}

export function startCallRingtone(id: string | number, volume: number): () => void {
  let stopped = false;
  let retryOnInteraction: (() => void) | null = null;
  const audio = getCallRingtoneAudio();
  const ringtoneId = String(id);

  const removeRetryListeners = () => {
    if (!retryOnInteraction) {
      return;
    }

    window.removeEventListener("pointerdown", retryOnInteraction);
    window.removeEventListener("keydown", retryOnInteraction);
    window.removeEventListener("touchstart", retryOnInteraction);
    retryOnInteraction = null;
  };

  const play = () => {
    if (stopped) {
      return;
    }

    if (activeCallRingtoneId && activeCallRingtoneId !== ringtoneId) {
      stopCallRingtone(activeCallRingtoneId);
    }

    audio.loop = true;
    audio.volume = clampVolume(volume) / 100;
    activeCallRingtoneId = ringtoneId;

    if (audio.paused) {
      audio.currentTime = 0;
    }

    audio.play().catch((error) => {
      if (stopped || retryOnInteraction) {
        return;
      }

      console.warn("Failed to play call ringtone:", error);
      retryOnInteraction = () => {
        removeRetryListeners();
        void play();
      };
      window.addEventListener("pointerdown", retryOnInteraction, { once: true });
      window.addEventListener("keydown", retryOnInteraction, { once: true });
      window.addEventListener("touchstart", retryOnInteraction, { once: true });
    });
  };

  if (activeCallRingtoneId !== ringtoneId) {
    audio.pause();
    audio.currentTime = 0;
  }
  play();

  return () => {
    stopped = true;
    removeRetryListeners();
    stopCallRingtone(ringtoneId);
  };
}
